import React from 'react';
import { Shield, Clock, MapPin, Monitor, FileText, User, Building } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const AuditTrailViewer = ({ auditData, onClose }) => {
  const { darkMode } = useTheme();

  if (!auditData) {
    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}>
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
          <div className="text-center">
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>No audit trail data available</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}>
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-center mb-4 relative">
          <div className="flex items-center">
            <Shield className={`h-6 w-6 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              E-SIGN Act Audit Trail
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`absolute right-0 p-2 rounded-full hover:bg-gray-200 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <div className="space-y-4">
          {/* Document Information */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
            <div className="flex items-center mb-2">
              <FileText className={`h-5 w-5 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Document Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Document Type:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.documentType || 'Non-Disclosure Agreement'}
                </span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Document ID:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.documentId || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Signature Information */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
            <div className="flex items-center mb-2">
              <User className={`h-5 w-5 mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Signature Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Signer Name:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.signatureData?.signerName || 'N/A'}
                </span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Signer Email:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.signatureData?.signerEmail || 'N/A'}
                </span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Company:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.signatureData?.companyName || 'N/A'}
                </span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Signature:</span>
                <span className={`ml-2 font-['Dancing_Script'] text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.signatureData?.signature || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* E-SIGN Act Compliance */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
            <div className="flex items-center mb-2">
              <Shield className={`h-5 w-5 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>E-SIGN Act Compliance</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${auditData.eSignCompliance?.consentToElectronicSignatures ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Electronic Signature Consent:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.eSignCompliance?.consentToElectronicSignatures ? 'Granted' : 'Not Granted'}
                </span>
              </div>
              <div className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 ${auditData.eSignCompliance?.consentToElectronicRecords ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Electronic Records Consent:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.eSignCompliance?.consentToElectronicRecords ? 'Granted' : 'Not Granted'}
                </span>
              </div>
              <div className="flex items-center">
                <span className={`w-3 h-3 rounded-full mr-2 bg-green-500`}></span>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Intent to Sign:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.eSignCompliance?.intentToSign ? 'Confirmed' : 'Not Confirmed'}
                </span>
              </div>
            </div>
          </div>

          {/* Audit Trail Details */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
            <div className="flex items-center mb-2">
              <Clock className={`h-5 w-5 mr-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Audit Trail Details</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1 text-gray-500" />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Signed:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.auditTrail?.signedDateTime ? new Date(auditData.auditTrail.signedDateTime).toLocaleString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1 text-gray-500" />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Consent:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.auditTrail?.consentTimestamp ? new Date(auditData.auditTrail.consentTimestamp).toLocaleString() : 'N/A'}
                </span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>IP Address:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.auditTrail?.ipAddress || 'N/A'}
                </span>
              </div>
              <div className="flex items-center">
                <Monitor className="h-4 w-4 mr-1 text-gray-500" />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Device:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.auditTrail?.device || 'N/A'}
                </span>
              </div>
              <div className="flex items-center">
                <Monitor className="h-4 w-4 mr-1 text-gray-500" />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Browser:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.auditTrail?.browser || 'N/A'}
                </span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Timezone:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.auditTrail?.timezone || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Document Integrity */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
            <div className="flex items-center mb-2">
              <Shield className={`h-5 w-5 mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Document Integrity</h3>
            </div>
            <div className="text-sm">
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Tamper-Proof Hash:</span>
                <span className={`ml-2 font-mono text-xs ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.recordIntegrity?.tamperProofHash || 'N/A'}
                </span>
              </div>
              <div className="mt-2">
                <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Snapshot Created:</span>
                <span className={`ml-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {auditData.recordIntegrity?.documentSnapshot?.snapshotTimestamp ? 
                    new Date(auditData.recordIntegrity.documentSnapshot.snapshotTimestamp).toLocaleString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailViewer; 